import { Chip, InputBase, Stack, Tooltip, Typography } from '@mui/material';
import SkeletonBox from 'components/generic/SkeletonBox';
import {
  memo,
  useCallback,
  useRef,
  useState,
  KeyboardEvent,
  ChangeEvent,
  useEffect,
} from 'react';
import {
  getChipsRenderChipSx,
  getChipsRenderTypoSx,
  getLabelSelectRenderChipSx,
  getSelectRenderValueTypoSx,
  InputChoice,
} from '../dropdownConsts';
import { SelectDropdownInputProps } from './SelectDropdownInput';
import { choiceTextString } from './selectUtils';
import { bloxValues } from 'themes/constants';
import { useTranslation } from 'react-i18next';

export interface RenderValueElementProps {
  selectedChoices: InputChoice[];
  placeholderText: string;
  renderValueDisplay: SelectDropdownInputProps['renderValueDisplay'];
  isLoading?: boolean;
  isError?: boolean;
  chipColor?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'info'
    | 'success'
    | 'warning';
  isDataPrivate?: boolean;
  group?: SelectDropdownInputProps['group'];
  /** `default: false` - allow users to create new items by typing in render value area */
  allowCreateNew?: boolean;
  /** callback fired when a new item value is submitted from inline input */
  onCreateNewItem?: (value: string) => void;
  /** whether the component is disabled */
  disabled?: boolean;
}

export const SelectRenderValueElem = memo(
  ({
    selectedChoices,
    placeholderText,
    renderValueDisplay,
    isLoading,
    isError,
    chipColor,
    isDataPrivate,
    group,
    allowCreateNew = false,
    onCreateNewItem,
    disabled = false,
  }: RenderValueElementProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();

    // Focus input when entering edit mode
    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
      }
    }, [isEditing]);

    const handleInputClick = useCallback(
      (e: React.MouseEvent) => {
        if (disabled) return;
        e.stopPropagation();
        setIsEditing(true);
      },
      [disabled],
    );

    const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    }, []);

    const handleInputKeyDown = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        // Prevent event bubbling to parent Select
        e.stopPropagation();

        if ((e.key === 'Enter' || e.key === 'Tab') && inputValue.trim()) {
          e.preventDefault();
          onCreateNewItem?.(inputValue.trim());
          setInputValue('');
          // Stay in edit mode - refocus input after state update
          setTimeout(() => {
            inputRef.current?.focus();
          }, 0);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setInputValue('');
          setIsEditing(false);
        }
      },
      [inputValue, onCreateNewItem],
    );

    const handleInputBlur = useCallback(() => {
      // Submit on blur if there's a value
      if (inputValue.trim()) {
        onCreateNewItem?.(inputValue.trim());
      }
      setInputValue('');
      setIsEditing(false);
    }, [inputValue, onCreateNewItem]);

    if (isLoading) {
      return <SkeletonBox height="min-content" />;
    }

    // Build existing chips JSX (used in multiple places)
    const buildExistingChipsJSX = selectedChoices.map((choice, idx) => (
      <Chip
        key={`chips-${choice.value}-${idx}`}
        size="small"
        variant="filled"
        label={choiceTextString(choice)}
        color={chipColor ?? (isError ? 'error' : 'primary')}
        sx={(theme) => getChipsRenderChipSx(theme, Boolean(isError), chipColor)}
        data-private={isDataPrivate ? true : undefined}
      />
    ));

    // Inline editing mode with chip-formatted input
    if (allowCreateNew && isEditing) {
      return (
        <Stack
          direction="row"
          className="render-value-chip-container"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.focus();
          }}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            width: '100%',
            overflow: 'hidden',
            cursor: 'text',
            minHeight: '24px',
          }}
        >
          {/* Show existing selected chips */}
          {buildExistingChipsJSX}

          {/* Draft chip with inline input */}
          <Tooltip
            open={Boolean(inputValue.length > 0) && isEditing}
            title={t('blox.editorial.syndication.labels.categories_hint')}
            placement="top"
          >
            <Chip
              size="small"
              variant="outlined"
              color="primary"
              sx={(theme) => ({
                ...getChipsRenderChipSx(theme, false, 'primary'),
                outlineStyle: 'dashed',
                outlineWidth: 1,
                outlineOffset: -1,
                border: 'unset',
              })}
              label={
                <InputBase
                  inputRef={inputRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  onBlur={handleInputBlur}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="Type..."
                  disabled={disabled}
                  data-private={isDataPrivate ? true : undefined}
                  sx={{
                    fontSize: bloxValues.tinyFontSizeRem,
                    minWidth: '40px',
                    '& .MuiInputBase-input': {
                      padding: 0,
                      fontSize: 'inherit',
                      minWidth: '40px',
                      width: `${Math.max(40, (inputValue.length + 1) * 6)}px`,
                    },
                  }}
                />
              }
              data-private={isDataPrivate ? true : undefined}
            />
          </Tooltip>
        </Stack>
      );
    }

    switch (renderValueDisplay) {
      case 'chips': {
        return (
          <Stack
            direction="row"
            className="render-value-chip-container"
            onClick={allowCreateNew ? handleInputClick : undefined}
            sx={[
              {
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              allowCreateNew && !disabled ? { cursor: 'text' } : undefined,
            ]}
          >
            {selectedChoices.length > 0 ? (
              buildExistingChipsJSX
            ) : (
              <Typography
                data-private={isDataPrivate ? true : undefined}
                sx={getChipsRenderTypoSx}
              >
                {placeholderText}
              </Typography>
            )}
          </Stack>
        );
      }
      case 'label':
      default: {
        return (
          <Stack
            direction="row"
            onClick={allowCreateNew ? handleInputClick : undefined}
            sx={[
              {
                alignItems: 'center',
                gap: 0.5,
              },
              allowCreateNew && !disabled ? { cursor: 'text', width: '100%' } : undefined,
            ]}
          >
            {selectedChoices.length > 0 && (
              <Chip
                color={isError ? 'error' : 'primary'}
                size="small"
                variant="filled"
                label={isError ? '!' : `${selectedChoices.length}`}
                clickable={false}
                sx={getLabelSelectRenderChipSx}
              />
            )}
            <Typography
              data-private={isDataPrivate ? true : undefined}
              sx={getSelectRenderValueTypoSx}
            >
              {placeholderText}
            </Typography>
          </Stack>
        );
      }
    }
  },
);
SelectRenderValueElem.displayName = 'SelectRenderValueElem';
